<template>
    <div class="device-item">
        <div class="device-item__icon">
            <span :class="deviceIcon"></span>
        </div>
        <div class="device-item__information">
            <h6 class="device-item__title">
                {{ fullDeviceName }}
            </h6>
            <p class="device-item__detail">
                <span class="device-item__label">{{ $t('Created') }}:</span>
                {{ createdDate }}
            </p>
            <p class="device-item__detail">
                <span class="device-item__label">{{ $t('Last used') }}:</span>
                {{ lastUsedText }}
            </p>
        </div>
        <div class="device-item__actions">
            <b-button 
                class="device-item__action-button" 
                @click="$emit('deleteApp', auth)"
                variant="link"
            >
                <span class="fas fa-trash"></span>
        </b-button>
        </div>
    </div>
</template>

<script>

export default {
    name: 'DeviceItem',
    props: {
        auth: {
            type: Object,
            required: true,
        }
    },
    computed: {
        deviceIcon() {
            const name = this.auth.name?.toLowerCase() || '';
            
            if (name.includes('windows hello') || name.includes('windows')) {
                return 'fab fa-windows device-item__icon-windows';
            }
            if (name.includes('android') || name.includes('galaxy') || name.includes('samsung')) {
                return 'fab fa-android device-item__icon-android';
            }
            if (name.includes('iphone') || name.includes('ipad') || name.includes('ios')) {
                return 'fab fa-apple device-item__icon-apple';
            }
            if (name.includes('mac') || name.includes('touch id') || name.includes('face id')) {
                return 'fab fa-apple device-item__icon-apple';
            }
            // Icono por defecto
            return 'fas fa-mobile-alt device-item__icon-default';
        },
        fullDeviceName() {
            // Mostrar el nombre completo con fecha como en la imagen
            // Si no hay nombre, mostrar un nombre por defecto basado en el tipo
            if (!this.auth.name || this.auth.name.trim() === '') {
                return this.$t('New Device');
            }
            return this.auth.name;
        },
        createdDate() {
            if (!this.auth.created_at) return '';
            const date = new Date(this.auth.created_at);
            // Formato: "June 19, 12:09 PM" o "August 14, 2024"
            const now = new Date();
            const isCurrentYear = date.getFullYear() === now.getFullYear();
            
            if (isCurrentYear) {
                // Para el año actual, mostrar "June 19, 12:09 PM"
                return this.formatDateWithTimeShort(date);
            }
            // Para años anteriores, mostrar "August 14, 2024"
            return this.formatDateShort(date);
        },
        lastUsedText() {
            if (!this.auth.last_used_at) {
                return this.$t('Not yet used');
            }
            //Regresar la fecha
            let format = new Date(this.auth.last_used_at).toLocaleString();
            format = format.slice(0, -3);
            return format;
        },
    },
    methods: {
        formatDateShort(date) {
            const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December'];
            const month = months[date.getMonth()];
            const day = date.getDate();
            const year = date.getFullYear();
            return `${month} ${day}, ${year}`;
        },
        formatDateWithTimeShort(date) {
            const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December'];
            const month = months[date.getMonth()];
            const day = date.getDate();
            let hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            return `${month} ${day}, ${hours}:${minutes} ${ampm}`;
        },
        formatDateWithTime(date) {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[date.getMonth()];
            const day = date.getDate();
            const year = date.getFullYear();
            let hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            return `${month} ${day}, ${year}, ${hours}:${minutes} ${ampm}`;
        }
    }
}
</script>

<style lang="scss">
.device-item {
    display: flex;
    align-items: flex-start;
    width: 100%;
    padding: 1rem 0;
    gap: 1rem;

    &__icon {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
        color: $accounts-secondary-200;

        &-windows,
        &-android,
        &-apple,
        &-default {
            color: $accounts-secondary-200;
        }
    }

    &__information {
        flex: 1;
        text-align: left;
        min-width: 0;
    }

    &__title {
        color: $general-black;
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
        line-height: 1.4;
    }

    &__detail {
        font-size: 0.875rem;
        color: $accounts-secondary-200;
        margin-bottom: 0.25rem;
        line-height: 1.5;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__label {
        font-weight: 500;
        margin-right: 0.25rem;
    }

    &__link {
        color: #0066cc;
        text-decoration: none;
        margin-left: 0.5rem;
        font-size: 0.875rem;

        &:hover {
            text-decoration: underline;
        }
    }

    &__android-info {
        display: block;
        margin-top: 0.25rem;
    }

    &__actions {
        display: flex;
        gap: 0.5rem;
        flex-shrink: 0;
        align-items: center;
    }

    &__action-button {
        background-color: transparent !important;
        border: none !important;
        padding: 0.5rem !important;
        color: $accounts-secondary-200 !important;
        font-size: 1rem !important;
        min-width: auto !important;
        height: auto !important;

        &:hover {
            color: $general-black !important;
        }

        span {
            display: block;
        }
    }
}
</style>